export type PeopleStatusCategory = "member" | "visitor" | "donor" | "vendor" | "prospect" | "other";

export interface PeopleStatusOption {
  id: string;
  label: string;
  category: PeopleStatusCategory;
}

export const defaultPeopleStatuses: PeopleStatusOption[] = [
  { id: "active_member", label: "Active member", category: "member" },
  { id: "inactive_member", label: "Inactive member", category: "member" },
  { id: "visitor", label: "Visitor", category: "visitor" },
  { id: "donor", label: "Donor", category: "donor" },
  { id: "vendor", label: "Vendor", category: "vendor" },
  { id: "prospect", label: "Prospect", category: "prospect" },
];

const toTitleCase = (value: string) =>
  value
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const validPeopleStatusCategories = new Set<PeopleStatusCategory>([
  "member",
  "visitor",
  "donor",
  "vendor",
  "prospect",
  "other",
]);

const statusCategoryAliases: Record<string, PeopleStatusCategory> = {
  ...Object.fromEntries(
    defaultPeopleStatuses.map((status) => [status.id, status.category])
  ),
  // Pre-2026-01 backfill stored is_member people as a bare "member" status,
  // before the active_member/inactive_member split.
  member: "member",
};

export const normalizePeopleStatuses = (
  options?: Array<PeopleStatusOption | string | null | undefined> | null
): PeopleStatusOption[] => {
  if (!options || options.length === 0) return defaultPeopleStatuses;

  const normalized = options.flatMap((option) => {
    if (typeof option === "string") {
      const id = option.trim();
      if (!id) return [];
      return [{
        id,
        label: toTitleCase(id),
        category: statusCategoryAliases[id.toLowerCase()] ?? "other",
      }];
    }

    if (!option?.id) return [];
    const id = option.id.trim();
    if (!id) return [];
    const category = validPeopleStatusCategories.has(option.category)
      ? option.category
      : "other";
    return [{
      id,
      label: option.label?.trim() || toTitleCase(id),
      category,
    }];
  });

  return normalized.length > 0 ? normalized : defaultPeopleStatuses;
};

export const getPeopleStatusOption = (
  status: string | null | undefined,
  options: PeopleStatusOption[]
) => options.find((option) => option.id === status) || null;

export const getPeopleStatusLabel = (
  status: string | null | undefined,
  options: PeopleStatusOption[]
) => {
  if (!status) return "";
  return getPeopleStatusOption(status, options)?.label || toTitleCase(status);
};

export const getPeopleStatusCategory = (
  status: string | null | undefined,
  options: PeopleStatusOption[]
): PeopleStatusCategory => {
  const matched = getPeopleStatusOption(status, options)?.category;
  if (matched) return matched;
  const legacy = status
    ? statusCategoryAliases[status.trim().toLowerCase()]
    : undefined;
  return legacy ?? "other";
};

export const getPeopleStatusBadgeClass = (category: PeopleStatusCategory) => {
  switch (category) {
    case "member":
      return "bg-muted text-muted-foreground";
    case "visitor":
      return "bg-secondary text-secondary-foreground";
    case "donor":
      return "bg-success-muted text-success";
    case "vendor":
      return "bg-info-muted text-info";
    case "prospect":
      return "bg-warning-muted text-warning";
    default:
      return "bg-muted text-muted-foreground";
  }
};

export const getPeopleStatusVariant = (category: PeopleStatusCategory) => {
  switch (category) {
    case "donor":
      return "success" as const;
    case "prospect":
      return "warning" as const;
    default:
      return "neutral" as const;
  }
};

export const statusMatchesCategory = (
  status: string | null | undefined,
  category: PeopleStatusCategory,
  options: PeopleStatusOption[]
) => getPeopleStatusCategory(status, options) === category;
